import { hasValue } from '../components/PreviewCards/utils';

import type {
  VehicleAccidentRecord,
  VehicleInvestigation,
  VehicleLienRecord,
  VehicleMarketValueDetails,
  VehicleOwnershipRecord,
  VehicleRecall,
  VehicleReportData,
  VehicleSafetyRating,
  VehicleSpecs,
  VehicleWarranty,
} from '../types';

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toString(value: unknown): string {
  return value == null ? '' : String(value);
}

function toBoolean(value: unknown): boolean {
  return Boolean(value);
}

function toNumberOrNull(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// Handles plain numbers ("19") and unit-suffixed strings ("18 miles/gallon")
function parseMpg(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  const n = parseFloat(String(value));
  return Number.isFinite(n) && n > 0 ? n : null;
}

const FUEL_TYPE_LABELS: Record<string, string> = {
  G: 'Gas',
  D: 'Diesel',
  E: 'Electric',
  H: 'Hybrid',
  P: 'Plug-in Hybrid',
  N: 'Natural Gas',
  B: 'Biodiesel',
  F: 'Flex Fuel',
};

function normalizeFuelType(value: string): string {
  const upper = value.trim().toUpperCase();
  return FUEL_TYPE_LABELS[upper] ?? value;
}

function normalizeCount(raw: unknown): number {
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
}

function mapRecalls(raw: unknown): VehicleRecall[] {
  return asArray(raw).map((item) => {
    const r = asRecord(item) ?? {};
    return {
      components: toString(r.components),
      date: toString(r.date),
      summary: toString(r.summary),
      consequence: toString(r.consequence),
      remedy: toString(r.remedy),
    };
  });
}

function mapWarranties(raw: unknown): VehicleWarranty[] {
  return asArray(raw).map((item) => {
    const r = asRecord(item) ?? {};
    return {
      months: toString(r.months),
      miles: toString(r.miles),
      type: toString(r.type),
      isActive: toBoolean(r.is_active),
    };
  });
}

function mapSafetyRatings(raw: unknown): VehicleSafetyRating[] {
  return asArray(raw).map((item) => {
    const r = asRecord(item) ?? {};
    return {
      vehicleDescription: toString(r.vehicle_description),
      overallRating: normalizeCount(r.overall_rating),
      frontCrashRating: normalizeCount(r.front_crash_rating),
      sideCrashRating: normalizeCount(r.side_crash_rating),
      rolloverRating: normalizeCount(r.rollover_rating),
    };
  });
}

function mapSpecs(raw: unknown): VehicleSpecs | undefined {
  const record = asRecord(raw);
  if (!record) {
    return undefined;
  }
  const engine = asRecord(record.engine) ?? {};
  const transmission = asRecord(record.transmission) ?? {};

  const cityMpg = parseMpg(record.city_mpg);
  const highwayMpg = parseMpg(record.highway_mpg);
  const fuelType = normalizeFuelType(toString(engine.fuel_type));
  const transmissionType = toString(transmission.type);
  const drivenWheels = toString(record.driven_wheels);

  if (
    !fuelType &&
    !transmissionType &&
    !drivenWheels &&
    cityMpg == null &&
    highwayMpg == null
  ) {
    return undefined;
  }

  return {
    cityMpg,
    highwayMpg,
    engineDisplacementLiters: toNumberOrNull(engine.displacement_liters),
    fuelType,
    transmissionType,
    transmissionGears: toNumberOrNull(transmission.gears),
    drivenWheels,
  };
}

function mapOwnershipHistory(raw: unknown): VehicleOwnershipRecord[] {
  return asArray(raw).map((item) => {
    const r = asRecord(item) ?? {};
    const odometer = toNumberOrNull(r.odometer);
    return {
      date: toString(r.date),
      isNew: Boolean(r.is_new),
      seller: toString(r.seller),
      cityState: toString(r.city_state),
      odometer: odometer !== null && odometer > 0 ? odometer : null,
      lienHolder: toString(r.lien_holder),
    };
  });
}

function mapLiens(raw: unknown): VehicleLienRecord[] {
  return asArray(raw).map((item) => {
    const r = asRecord(item) ?? {};
    return {
      date: toString(r.date),
      lienholder: toString(r.lienholder),
    };
  });
}

function mapAccidents(raw: unknown): VehicleAccidentRecord[] {
  return asArray(raw).map((item) => {
    const r = asRecord(item) ?? {};
    return {
      date: toString(r.date),
      location: toString(r.location),
      mannerOfCollision: toString(r.manner_of_collision),
      crashSeverity: toString(r.crash_severity),
    };
  });
}

function mapInvestigations(raw: unknown): VehicleInvestigation[] {
  return asArray(raw).map((item) => {
    const r = asRecord(item) ?? {};
    return {
      nhtsaActionNumber: toString(r.nhtsa_action_number),
      subject: toString(r.subject),
      summary: toString(r.summary),
      components: asArray(r.components).map((c) => toString(c)),
    };
  });
}

function mapMarketValueDetails(
  raw: unknown
): VehicleMarketValueDetails | undefined {
  const r = asRecord(raw);
  if (!r) {
    return undefined;
  }
  const priceRange = asRecord(r.price_range) ?? {};
  return {
    msrp: toNumberOrNull(r.msrp),
    priceRangeAverage: toNumberOrNull(priceRange.average),
    priceRangeHighest: toNumberOrNull(priceRange.highest),
    priceRangeLowest: toNumberOrNull(priceRange.lowest),
  };
}

/**
 * A vehicle report is "empty" when there is nothing to show besides the header /
 * permalink: every record count is zero and there is no value, mileage, specs,
 * or safety information. `searchQuery` is intentionally ignored since it only
 * drives the card's title. Used to suppress rendering empty deepsearch previews.
 */
export function isVehicleReportEmpty(data: VehicleReportData): boolean {
  const hasRecords =
    data.ownershipHistory > 0 ||
    data.accidents > 0 ||
    data.damageHistory > 0 ||
    data.salesListings > 0 ||
    data.liens > 0 ||
    data.recalls > 0 ||
    data.ownershipCostRecords > 0 ||
    data.warrantyRecords > 0 ||
    data.activeWarranties > 0 ||
    Boolean(data.openInvestigations);

  const mv = data.marketValueDetails;
  const hasValueInfo =
    hasValue(data.marketValue) ||
    hasValue(data.estimatedYearlyCost) ||
    hasValue(data.lastReportedMileage) ||
    hasValue(data.estimatedMileage) ||
    Boolean(
      mv &&
        ((mv.msrp ?? 0) > 0 ||
          (mv.priceRangeAverage ?? 0) > 0 ||
          (mv.priceRangeHighest ?? 0) > 0 ||
          (mv.priceRangeLowest ?? 0) > 0)
    );

  const hasDetails =
    hasValue(data.vin) ||
    hasValue(data.bodyStyle) ||
    Boolean(data.specs) ||
    Boolean(data.safetyRatings && data.safetyRatings.length > 0);

  return !hasRecords && !hasValueInfo && !hasDetails;
}

export function mapVehicleReportPayload(
  raw: Record<string, unknown>,
  subtype: 'ymm' | 'vin' | 'plate'
): VehicleReportData {
  const damages = asRecord(raw.damages) ?? {};
  const safety = asRecord(raw.safety) ?? {};
  const background = asRecord(raw.background) ?? {};

  const recallDetails = mapRecalls(damages.recalls);
  const warrantyDetails = mapWarranties(safety.warranties);
  const safetyRatings = mapSafetyRatings(safety.safety_ratings);
  const specs = mapSpecs(asRecord(raw.specs)?.specs);
  const marketValueDetails = mapMarketValueDetails(raw.marketValueDetails);
  const ownershipHistoryDetails = mapOwnershipHistory(
    background.ownership_history
  );
  const lienDetails = mapLiens(background.liens);
  const accidentDetails = mapAccidents(damages.accidents);
  const investigationDetails = mapInvestigations(
    background.open_investigations
  );
  const openInvestigations = investigationDetails.length;

  return {
    subtype,
    searchQuery: toString(raw.searchQuery),
    bodyStyle: toString(raw.bodyStyle),
    vin: (() => {
      const v = toString(raw.vin);
      return /^X+$/i.test(v) ? '' : v;
    })(),
    lastReportedMileage:
      subtype === 'vin' || subtype === 'plate'
        ? toString(raw.lastReportedMileage)
        : '',
    lastReportedMileageDate:
      subtype === 'vin' || subtype === 'plate'
        ? toString(raw.lastReportedMileageDate)
        : '',
    estimatedMileage: toString(raw.estimatedMileage),
    mileageAsOfDate: toString(raw.mileageAsOfDate),
    marketValue: toString(raw.marketValue),
    estimatedYearlyCost: toString(raw.estimatedYearlyCost),
    ownershipHistory: normalizeCount(raw.ownershipHistory),
    accidents: normalizeCount(raw.accidents),
    damageHistory: normalizeCount(raw.damageHistory),
    salesListings:
      subtype === 'vin' || subtype === 'plate'
        ? normalizeCount(raw.salesListings)
        : 0,
    liens: normalizeCount(raw.liens),
    recalls: normalizeCount(raw.recalls),
    ownershipCostRecords:
      subtype === 'ymm' ? normalizeCount(raw.ownershipCostRecords) : 0,
    warrantyRecords: normalizeCount(raw.warrantyRecords),
    activeWarranties: normalizeCount(raw.activeWarranties),
    recallDetails: recallDetails.length > 0 ? recallDetails : undefined,
    warrantyDetails: warrantyDetails.length > 0 ? warrantyDetails : undefined,
    safetyRatings: safetyRatings.length > 0 ? safetyRatings : undefined,
    specs,
    marketValueDetails,
    openInvestigations: openInvestigations > 0 ? openInvestigations : undefined,
    ownershipHistoryDetails:
      ownershipHistoryDetails.length > 0 ? ownershipHistoryDetails : undefined,
    lienDetails: lienDetails.length > 0 ? lienDetails : undefined,
    accidentDetails: accidentDetails.length > 0 ? accidentDetails : undefined,
    investigationDetails:
      investigationDetails.length > 0 ? investigationDetails : undefined,
  };
}
